import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FolderOpen, Refresh } from '@mui/icons-material';
import { platformApi, PublishingRequestDashboardItem } from '../services/platformApi';
import { getStatusColor } from './projectConstants';
import { useLanguage } from '../LanguageContext';
import { formatTranslatedLabel } from '../i18n';

const statusOptions = [
  'all',
  'package_ready',
  'ready',
  'submitted',
  'changes_requested',
  'approved',
  'published',
  'rejected',
  'cancelled',
];

const providerOptions = [
  { value: 'all', label: 'All providers' },
  { value: 'amazon_kdp', label: 'Amazon KDP' },
  { value: 'other_provider', label: 'Other Provider' },
];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const PublishingDashboard: React.FC = () => {
  const { locale, t } = useLanguage();
  const [requests, setRequests] = useState<PublishingRequestDashboardItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const requestData = await platformApi.listPublishingRequests({
        status: statusFilter === 'all' ? undefined : statusFilter,
        provider: providerFilter === 'all' ? undefined : providerFilter,
      });
      setRequests(requestData);
    } catch (err) {
      setError(t('couldNotLoadPublishingRequests'));
    } finally {
      setLoading(false);
    }
  }, [providerFilter, statusFilter, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = useCallback(
    (value?: string | null) => (value ? new Date(value).toLocaleString(locale) : t('notRecorded')),
    [locale, t],
  );

  const stats = useMemo(() => {
    const counts = requests.reduce<Record<string, number>>((grouped, request) => {
      grouped[request.status] = (grouped[request.status] || 0) + 1;
      return grouped;
    }, {});
    return [
      { label: t('totalRequests'), value: requests.length },
      { label: t('ready'), value: (counts.package_ready || 0) + (counts.ready || 0) },
      { label: t('submitted'), value: counts.submitted || 0 },
      { label: t('published'), value: counts.published || 0 },
    ];
  }, [requests, t]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('publishingDashboard')}
          </Typography>
          <Typography color="text.secondary">
            {t('publishingDashboardDescription')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label={t('refreshPublishingRequests')} onClick={loadData}>
            <Refresh />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 92 }}>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
              <Typography variant="h4" component="div">
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="publishing-status-filter-label">{t('status')}</InputLabel>
            <Select
              labelId="publishing-status-filter-label"
              label={t('status')}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status === 'all' ? t('allStatuses') : formatTranslatedLabel(t, status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="publishing-provider-filter-label">{t('provider')}</InputLabel>
            <Select
              labelId="publishing-provider-filter-label"
              label={t('provider')}
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value)}
            >
              {providerOptions.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.value === 'all' ? t('allProviders') : formatTranslatedLabel(t, provider.value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('project')}</TableCell>
              <TableCell>{t('book')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('provider')}</TableCell>
              <TableCell>{t('package')}</TableCell>
              <TableCell>{t('submittedDate')}</TableCell>
              <TableCell>{t('reference')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => {
              const checklist = asRecord(asRecord(request.metadata_json).submission_checklist);
              const packageReady = Boolean(checklist.package_ready);
              const preflightPassed = Boolean(checklist.preflight_passed);
              return (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{request.project_title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Project {request.project_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{request.book_title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTranslatedLabel(t, request.export_type)} / {formatTranslatedLabel(t, request.export_status)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={formatTranslatedLabel(t, request.status)} color={getStatusColor(request.status)} />
                  </TableCell>
                  <TableCell>{formatTranslatedLabel(t, request.provider || 'publishing')}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={t('packageReady')}
                        color={packageReady ? 'success' : 'warning'}
                        variant={packageReady ? 'filled' : 'outlined'}
                      />
                      <Chip
                        size="small"
                        label={t('preflight')}
                        color={preflightPassed ? 'success' : 'warning'}
                        variant={preflightPassed ? 'filled' : 'outlined'}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(request.submitted_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.external_reference || request.asin || request.isbn || t('none')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {request.marketplace || t('noMarketplace')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      component={RouterLink}
                      to={`/projects/${request.project_id}`}
                      variant="text"
                      startIcon={<FolderOpen />}
                    >
                      {t('open')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {loading ? t('loadingPublishingRequests') : t('noPublishingRequestsMatch')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PublishingDashboard;
